import { createRoot } from 'react-dom/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HostIdentityContext, HostMenuExtras, type HostIdentity } from 'src/front-components/lead-desk/host';
import page from 'src/front-components/lead-desk-page';
import { AuthPage, LoginPage, SetupPage, type SessionUser } from './login';
import { setChoice, useChoice, useResolvedScheme, type ThemeChoice } from './theme';

const Desk = page.component;

const CHOICES: ReadonlyArray<{ id: ThemeChoice; label: string }> = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' },
];

// A compact segmented control inside the account menu. Drawn from the desk's own tokens.
const ThemeSwitch = () => {
  const choice = useChoice();

  return (
    <div role="group" aria-label="Appearance" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 'var(--t-font-size-sm)', color: 'var(--t-font-color-tertiary)' }}>Theme</span>
      <span style={{ display: 'inline-flex', padding: 2, gap: 1, borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-background-tertiary)' }}>
        {CHOICES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setChoice(c.id)}
            aria-pressed={choice === c.id}
            style={{
              padding: '2px 9px',
              border: 'none',
              borderRadius: 'var(--t-border-radius-xs)',
              cursor: 'pointer',
              fontSize: 'var(--t-font-size-sm)',
              lineHeight: 1.5,
              background: choice === c.id ? 'var(--t-background-primary)' : 'transparent',
              color: choice === c.id ? 'var(--t-font-color-primary)' : 'var(--t-font-color-tertiary)',
              boxShadow: choice === c.id ? '0 0 0 1px var(--t-border-color-medium)' : 'none',
              fontWeight: choice === c.id ? 600 : 400,
            }}
          >
            {c.label}
          </button>
        ))}
      </span>
    </div>
  );
};

// Every fetch from the desk goes through here. A 401 anywhere means the session ended (signed out elsewhere, expired,
// deactivated), so the whole app drops back to the sign-in page instead of every screen showing its own error.
const installSessionWatch = (onExpired: () => void): (() => void) => {
  const original = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await original(...args);
    const url = String(typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : args[0]);

    if (response.status === 401 && url.includes('/api/') && (!url.includes('/api/auth/') || url.endsWith('/api/auth/me'))) onExpired();

    return response;
  };

  return () => {
    window.fetch = original;
  };
};

type Boot = { phase: 'loading' } | { phase: 'setup' } | { phase: 'login'; notice: string | null } | { phase: 'ready'; user: SessionUser };

const App = () => {
  useResolvedScheme();

  const [boot, setBoot] = useState<Boot>({ phase: 'loading' });

  const start = useCallback(async (): Promise<void> => {
    try {
      const me = await fetch('/api/auth/me');

      if (me.ok) return setBoot({ phase: 'ready', user: (await me.json()) as SessionUser });

      const status = (await (await fetch('/api/auth/status')).json()) as { setupNeeded?: boolean };

      setBoot(status.setupNeeded === true ? { phase: 'setup' } : { phase: 'login', notice: null });
    } catch {
      setBoot({ phase: 'login', notice: 'Cannot reach the server. Check that it is running, then reload.' });
    }
  }, []);

  useEffect(() => {
    void start();

    const stopWatching = installSessionWatch(() => setBoot((now) => (now.phase === 'ready' ? { phase: 'login', notice: 'You were signed out. Sign in again to continue.' } : now)));

    // Re-check the session when the person comes back to the tab and whenever they click, so an ended session shows at once instead of
    // after the next 15-second refresh. A 401 from this call goes through the same watcher above.
    let last = 0;
    const recheck = (): void => {
      if (Date.now() - last < 2000) return;

      last = Date.now();
      void fetch('/api/auth/me');
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') recheck();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', recheck);
    document.addEventListener('click', recheck, true);

    return () => {
      stopWatching();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('click', recheck, true);
    };
  }, [start]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } finally {
      setBoot({ phase: 'login', notice: null });
    }
  }, []);

  const identity = useMemo<HostIdentity | null>(() => {
    if (boot.phase !== 'ready') return null;

    const u = boot.user;

    return {
      id: u.id,
      role: u.role,
      firstName: u.firstName,
      lastName: u.lastName,
      name: `${u.firstName} ${u.lastName}`.trim(),
      email: u.email,
      onSignOut: () => void signOut(),
      refresh: start,
    };
  }, [boot, signOut, start]);

  if (boot.phase === 'loading') return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t-font-color-tertiary)' }}>Loading…</div>;
  if (boot.phase === 'setup' || boot.phase === 'login') return <AuthPage notice={boot.phase === 'login' ? boot.notice : null} onSignedIn={(user) => setBoot({ phase: 'ready', user })} />;


  return (
    <HostIdentityContext.Provider value={identity}>
      <HostMenuExtras.Provider value={<ThemeSwitch />}>
        {/* keyed on the person, so signing in as someone else starts from a clean screen and clean state */}
        <Desk key={boot.user.id} />
      </HostMenuExtras.Provider>
    </HostIdentityContext.Provider>
  );
};

createRoot(document.getElementById('root') as HTMLElement).render(<App />);
